import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Clock,
  Copy,
  MailX,
  MoreHorizontal,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  Trash2,
  UserMinus,
  Users,
  XCircle,
} from "lucide-react";
import {
  canEditMemberDisplayName,
  canManageInvitation,
  canRemoveMember,
  type TeamInvitation,
  type TeamMember,
} from "@multi-chat/shared";
import { isApiError } from "../../api/client.js";
import { teamRenewLimitMessage } from "../team/team-errors.js";
import {
  cancelTeamInvitation,
  getTeam,
  removeMember,
  resendTeamInvitation,
  updateMemberDisplayName,
} from "../../api/team-api.js";
import { ProductErrorState } from "../../components/common/ProductErrorState.js";
import { ListSkeleton } from "../../components/common/WorkspaceLoader.js";
import { LtrValue } from "../../components/common/LtrValue.js";
import { Badge } from "../../components/ui/badge/index.js";
import { Button } from "../../components/ui/button/index.js";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu/index.js";
import {
  AlertDialog,
  AlertDialogActions,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog/index.js";
import { cn } from "../../lib/utils.js";
import { useAuth } from "../auth/auth-context.js";
import { useNotifications } from "../notifications/notification-context.js";
import { SettingsSectionPage } from "./SettingsSectionPage.js";
import { useSettingsWorkspace } from "./SettingsWorkspaceLayout.js";
import { InviteMembersDialog } from "../team/InviteMembersDialog.js";

type ConfirmState =
  | { kind: "remove"; member: TeamMember }
  | { kind: "cancel"; invitation: TeamInvitation }
  | null;

function memberInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const letters = parts.slice(0, 2).map((part) => part.charAt(0));
  return (letters.join("") || name.charAt(0) || "?").toUpperCase();
}

function roleBadgeClass(role: TeamMember["role"] | TeamInvitation["role"]): string {
  switch (role) {
    case "OWNER":
      return "border border-[#132a27]/20 bg-[#132a27] font-semibold text-[#34d399]";
    case "ADMIN":
      return "border border-accent/30 bg-accent-soft font-semibold text-accent";
    case "AGENT":
      return "border border-border bg-surface-muted font-medium text-muted";
    default: {
      const _exhaustive: never = role;
      return _exhaustive;
    }
  }
}

function statusBadgeProps(
  status: TeamMember["status"] | TeamInvitation["status"],
): { variant: "success" | "warning" | "error" | "default"; className?: string } {
  switch (status) {
    case "ACTIVE":
      return { variant: "success", className: "font-semibold" };
    case "PENDING":
      return { variant: "warning", className: "font-semibold" };
    case "SUSPENDED":
      return { variant: "error", className: "font-semibold" };
    case "EXPIRED":
      return {
        variant: "default",
        className: "border border-warning/25 bg-warning-muted font-semibold text-warning",
      };
    case "CANCELLED":
      return { variant: "default", className: "font-semibold" };
    default: {
      const _exhaustive: never = status;
      return _exhaustive;
    }
  }
}

export function TeamSettingsPage(): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const { organizationId, role } = useSettingsWorkspace();
  const { applyMemberDisplayName } = useAuth();
  const { pushNotification } = useNotifications();
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [invitations, setInvitations] = useState<TeamInvitation[]>([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [confirm, setConfirm] = useState<ConfirmState>(null);
  const [nameTarget, setNameTarget] = useState<TeamMember | null>(null);
  const [nameValue, setNameValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [openMenuKey, setOpenMenuKey] = useState<string | null>(null);

  const reload = useCallback(() => setReloadKey((value) => value + 1), []);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    setMembers([]);
    setInvitations([]);
    setOpenMenuKey(null);
    void getTeam(organizationId, controller.signal)
      .then((team) => {
        if (controller.signal.aborted) return;
        setMembers(team.members);
        setInvitations(team.invitations);
        setLoading(false);
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        setError(isApiError(caught) ? caught.message : t("team.loadError"));
        setLoading(false);
      });
    return () => controller.abort();
  }, [organizationId, reloadKey, t]);

  const needle = query.trim().toLowerCase();
  const visibleMembers = useMemo(
    () =>
      members.filter((member) =>
        needle.length === 0
          ? true
          : `${member.displayName} ${member.email}`.toLowerCase().includes(needle),
      ),
    [members, needle],
  );
  const visibleInvites = useMemo(
    () =>
      invitations.filter((invitation) => {
        if (invitation.status === "CANCELLED") return false;
        if (needle.length === 0) return true;
        return `${invitation.displayName} ${invitation.email}`.toLowerCase().includes(needle);
      }),
    [invitations, needle],
  );

  async function confirmAction(): Promise<void> {
    if (!confirm) return;
    setBusy(true);
    try {
      if (confirm.kind === "remove") {
        await removeMember(organizationId, confirm.member.membershipId);
        pushNotification({ message: t("team.memberRemoved") });
      } else {
        await cancelTeamInvitation(organizationId, confirm.invitation.invitationId);
        pushNotification({ message: t("team.invite.cancelled") });
      }
      setConfirm(null);
      reload();
    } catch (caught) {
      pushNotification({
        kind: "error",
        message: isApiError(caught) ? caught.message : t("team.actionError"),
      });
    } finally {
      setBusy(false);
    }
  }

  async function resend(invitation: TeamInvitation): Promise<void> {
    setBusy(true);
    try {
      await resendTeamInvitation(organizationId, invitation.invitationId);
      pushNotification({
        message: t("team.invite.resent", { email: invitation.email }),
      });
      reload();
    } catch (caught) {
      const limitMessage = teamRenewLimitMessage(t, caught);
      pushNotification({
        kind: "error",
        message:
          limitMessage ?? (isApiError(caught) ? caught.message : t("team.actionError")),
      });
    } finally {
      setBusy(false);
    }
  }

  async function copyEmail(email: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(email);
      pushNotification({ message: t("team.copied") });
    } catch {
      pushNotification({ kind: "error", message: t("team.copyFailed") });
    }
  }

  const dateFormat = new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium" });

  return (
    <SettingsSectionPage
      testId="settings-team-page"
      icon={Users}
      title={t("settings.team.title")}
      description={t("settings.team.description")}
      actions={
        role === "OWNER" ? (
          <Button
            size="sm"
            className="gap-1.5 rounded-xl px-3.5 font-semibold shadow-xs"
            onClick={() => setInviteOpen(true)}
            data-testid="team-invite-open"
          >
            <Plus size={16} aria-hidden="true" />
            {t("team.invite.cta")}
          </Button>
        ) : null
      }
    >
      <div className="mx-auto flex w-full max-w-[68rem] flex-col gap-5">
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-line/60 bg-canvas/60 p-3">
          <label className="relative block w-full sm:max-w-[22rem]">
            <Search
              size={15}
              className="pointer-events-none absolute start-3 top-1/2 -translate-y-1/2 text-muted"
              aria-hidden="true"
            />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={t("team.search")}
              className="h-9.5 w-full rounded-xl border border-line bg-surface ps-9 pe-3.5 text-[0.8125rem] transition-shadow focus:border-[#163a37] focus:shadow-[0_0_0_3px_rgb(52_211_153/0.18)] focus:outline-none"
              data-testid="team-search"
            />
          </label>

          {!loading && !error ? (
            <div className="flex flex-wrap items-center gap-2 text-[0.75rem]">
              <span className="inline-flex items-center gap-1.5 rounded-xl border border-line/70 bg-surface px-3 py-1.5 font-medium text-foreground">
                <span className="size-2 rounded-full bg-[#10b981]" />
                <span className="font-semibold tabular-nums">{visibleMembers.length}</span>
                <span className="text-muted">{t("settings.team.title")}</span>
              </span>
              {visibleInvites.length > 0 ? (
                <span className="inline-flex items-center gap-1.5 rounded-xl border border-amber-500/25 bg-amber-500/10 px-3 py-1.5 font-medium text-amber-800">
                  <Clock size={13} aria-hidden="true" />
                  <span className="font-semibold tabular-nums">{visibleInvites.length}</span>
                  <span>{t("team.status.pending")}</span>
                </span>
              ) : null}
            </div>
          ) : null}
        </div>

        {loading ? <ListSkeleton rows={5} label={t("app.loading")} testId="team-loading" /> : null}
        {error ? (
          <ProductErrorState
            title={t("team.loadErrorTitle")}
            description={error}
            onRetry={reload}
            retryLabel={t("settings.general.retry")}
            testId="team-load-error"
          />
        ) : null}

        {!loading && !error ? (
          <div className="grid gap-6">
            {visibleMembers.length > 0 ? (
              <div className="divide-y divide-line/60 overflow-hidden rounded-2xl border border-line/70 bg-surface shadow-[0_8px_28px_-18px_rgb(19_42_39/0.12)]">
                {visibleMembers.map((member) => {
                  const removable =
                    canRemoveMember(role, member.role) && member.status === "ACTIVE";
                  const canEditName = canEditMemberDisplayName(
                    role,
                    member.role,
                    member.isCurrentUser,
                  );
                  const isOwnerRow = member.role === "OWNER";
                  const hasMenu = canEditName || removable || !isOwnerRow;
                  const menuKey = `member-${member.membershipId}`;
                  const status = statusBadgeProps(member.status);

                  return (
                    <article
                      key={member.membershipId}
                      className="flex items-center gap-3.5 px-4 py-3.5 transition-colors hover:bg-canvas/40 sm:px-5"
                      data-testid={`team-member-${member.membershipId}`}
                    >
                      <span
                        className={cn(
                          "inline-grid size-11 shrink-0 place-items-center rounded-2xl text-[0.8125rem] font-bold tracking-wide",
                          isOwnerRow
                            ? "bg-[#132a27] text-[#34d399] ring-1 ring-[#34d399]/30"
                            : "bg-[#163a37]/10 text-[#163a37]",
                        )}
                        aria-hidden="true"
                      >
                        {memberInitials(member.displayName)}
                      </span>

                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="m-0 truncate text-[0.9375rem] font-semibold tracking-[-0.01em] text-foreground">
                            {member.displayName}
                          </p>
                          {member.isCurrentUser ? (
                            <span className="rounded-md bg-[#34d399]/15 px-1.5 py-0.5 text-[0.6875rem] font-semibold text-[#132a27]">
                              {t("team.you")}
                            </span>
                          ) : null}
                        </div>
                        <p className="m-0 mt-0.5 truncate text-[0.75rem] text-muted">
                          <LtrValue>{member.email}</LtrValue>
                        </p>
                        <div className="mt-1.5 flex flex-wrap items-center gap-1.5 sm:hidden">
                          <Badge
                            className={cn(
                              "rounded-lg px-2 py-0.5 text-[0.68rem]",
                              roleBadgeClass(member.role),
                            )}
                          >
                            {t(`organization.role.${member.role.toLowerCase()}`)}
                          </Badge>
                          <Badge
                            variant={status.variant}
                            className={cn("rounded-lg px-2 py-0.5 text-[0.68rem]", status.className)}
                          >
                            {member.status === "SUSPENDED"
                              ? t("team.status.suspended")
                              : t("team.status.active")}
                          </Badge>
                        </div>
                      </div>

                      <div className="hidden flex-wrap items-center gap-1.5 sm:flex">
                        <Badge
                          className={cn(
                            "rounded-lg px-2.5 py-0.5 text-[0.7rem]",
                            roleBadgeClass(member.role),
                          )}
                        >
                          {t(`organization.role.${member.role.toLowerCase()}`)}
                        </Badge>
                        <Badge
                          variant={status.variant}
                          className={cn("rounded-lg px-2.5 py-0.5 text-[0.7rem]", status.className)}
                        >
                          {member.status === "SUSPENDED"
                            ? t("team.status.suspended")
                            : t("team.status.active")}
                        </Badge>
                      </div>

                      <div className="shrink-0">
                        {hasMenu ? (
                          <DropdownMenu
                            open={openMenuKey === menuKey}
                            onOpenChange={(open) => setOpenMenuKey(open ? menuKey : null)}
                          >
                            <DropdownMenuTrigger asChild>
                              <button
                                type="button"
                                className="inline-flex size-8 items-center justify-center rounded-lg border-0 bg-transparent text-muted transition-colors hover:bg-surface-muted hover:text-foreground"
                                aria-label={t("team.columns.actions")}
                                data-testid={`team-member-menu-${member.membershipId}`}
                              >
                                <MoreHorizontal size={16} aria-hidden="true" />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="min-w-[11.5rem]">
                              {canEditName ? (
                                <DropdownMenuItem
                                  onSelect={() => {
                                    setOpenMenuKey(null);
                                    setNameTarget(member);
                                    setNameValue(member.displayName);
                                  }}
                                >
                                  <Pencil size={14} aria-hidden="true" />
                                  {t("team.editName")}
                                </DropdownMenuItem>
                              ) : null}
                              {!isOwnerRow ? (
                                <DropdownMenuItem
                                  onSelect={() => {
                                    setOpenMenuKey(null);
                                    void copyEmail(member.email);
                                  }}
                                >
                                  <Copy size={14} aria-hidden="true" />
                                  {t("team.copyEmail")}
                                </DropdownMenuItem>
                              ) : null}
                              {removable ? (
                                <DropdownMenuItem
                                  className="text-error data-[highlighted]:bg-error-muted data-[highlighted]:text-error"
                                  onSelect={() => {
                                    setOpenMenuKey(null);
                                    setConfirm({ kind: "remove", member });
                                  }}
                                >
                                  <Trash2 size={14} aria-hidden="true" />
                                  {t("team.remove")}
                                </DropdownMenuItem>
                              ) : null}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        ) : null}
                      </div>
                    </article>
                  );
                })}
              </div>
            ) : null}

            {visibleInvites.length > 0 ? (
              <section className="grid gap-2.5">
                <div className="flex items-center gap-2 px-1">
                  <Clock size={14} className="text-muted" aria-hidden="true" />
                  <h2 className="m-0 text-[0.75rem] font-bold tracking-[0.08em] text-muted uppercase">
                    {t("team.status.pending")}
                  </h2>
                </div>
                <div className="grid gap-2">
                  {visibleInvites.map((invitation) => {
                    if (invitation.status === "CANCELLED") return null;
                    const allowed = canManageInvitation(role, invitation.role);
                    const canResend =
                      allowed &&
                      (invitation.status === "PENDING" || invitation.status === "EXPIRED");
                    const canCancel = allowed && invitation.status === "PENDING";
                    const statusLabel =
                      invitation.status === "PENDING"
                        ? t("team.status.pending")
                        : invitation.status === "EXPIRED"
                          ? t("team.status.expired")
                          : t("team.status.cancelled");
                    const menuKey = `invite-${invitation.invitationId}`;
                    const status = statusBadgeProps(invitation.status);

                    return (
                      <article
                        key={`invite-${invitation.invitationId}`}
                        className="flex items-center gap-3.5 rounded-2xl border border-dashed border-[#163a37]/25 bg-[#163a37]/[0.025] px-4 py-3.5 sm:px-5"
                        data-testid={`team-invite-${invitation.invitationId}`}
                      >
                        <span
                          className="inline-grid size-11 shrink-0 place-items-center rounded-2xl border border-[#163a37]/20 bg-surface text-[0.8125rem] font-bold text-[#163a37]"
                          aria-hidden="true"
                        >
                          {memberInitials(invitation.displayName)}
                        </span>
                        <div className="min-w-0 flex-1">
                          <p className="m-0 truncate text-[0.9375rem] font-semibold text-foreground">
                            {invitation.displayName}
                          </p>
                          <p className="m-0 truncate text-[0.75rem] text-muted">
                            <LtrValue>{invitation.email}</LtrValue>
                          </p>
                          <p className="m-0 mt-1 text-[0.7rem] text-muted">
                            {t("team.expires", {
                              date: dateFormat.format(new Date(invitation.expiresAt)),
                            })}
                          </p>
                          <div className="mt-1.5 flex flex-wrap items-center gap-1.5 sm:hidden">
                            <Badge
                              className={cn(
                                "rounded-lg px-2 py-0.5 text-[0.68rem]",
                                roleBadgeClass(invitation.role),
                              )}
                            >
                              {t(`organization.role.${invitation.role.toLowerCase()}`)}
                            </Badge>
                            <Badge
                              variant={status.variant}
                              className={cn("rounded-lg px-2 py-0.5 text-[0.68rem]", status.className)}
                            >
                              {statusLabel}
                            </Badge>
                          </div>
                        </div>
                        <div className="hidden flex-wrap items-center gap-1.5 sm:flex">
                          <Badge
                            className={cn(
                              "rounded-lg px-2.5 py-0.5 text-[0.7rem]",
                              roleBadgeClass(invitation.role),
                            )}
                          >
                            {t(`organization.role.${invitation.role.toLowerCase()}`)}
                          </Badge>
                          <Badge
                            variant={status.variant}
                            className={cn("rounded-lg px-2.5 py-0.5 text-[0.7rem]", status.className)}
                          >
                            {statusLabel}
                          </Badge>
                        </div>
                        <div className="shrink-0">
                          {canResend || canCancel ? (
                            <DropdownMenu
                              open={openMenuKey === menuKey}
                              onOpenChange={(open) => setOpenMenuKey(open ? menuKey : null)}
                            >
                              <DropdownMenuTrigger asChild>
                                <button
                                  type="button"
                                  className="inline-flex size-8 items-center justify-center rounded-lg border-0 bg-transparent text-muted hover:bg-surface-muted hover:text-foreground"
                                  aria-label={t("team.columns.actions")}
                                  data-testid={`team-invite-menu-${invitation.invitationId}`}
                                >
                                  <MoreHorizontal size={16} aria-hidden="true" />
                                </button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="min-w-[11.5rem]">
                                {canResend ? (
                                  <DropdownMenuItem
                                    onSelect={() => {
                                      setOpenMenuKey(null);
                                      void resend(invitation);
                                    }}
                                  >
                                    <RefreshCw size={14} aria-hidden="true" />
                                    {invitation.status === "EXPIRED"
                                      ? t("team.renew")
                                      : t("team.resend")}
                                  </DropdownMenuItem>
                                ) : null}
                                <DropdownMenuItem
                                  onSelect={() => {
                                    setOpenMenuKey(null);
                                    void copyEmail(invitation.email);
                                  }}
                                >
                                  <Copy size={14} aria-hidden="true" />
                                  {t("team.copyEmail")}
                                </DropdownMenuItem>
                                {canCancel ? (
                                  <DropdownMenuItem
                                    className="text-error data-[highlighted]:bg-error-muted data-[highlighted]:text-error"
                                    onSelect={() => {
                                      setOpenMenuKey(null);
                                      setConfirm({ kind: "cancel", invitation });
                                    }}
                                  >
                                    <XCircle size={14} aria-hidden="true" />
                                    {t("team.cancelInvite")}
                                  </DropdownMenuItem>
                                ) : null}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          ) : null}
                        </div>
                      </article>
                    );
                  })}
                </div>
              </section>
            ) : null}

            {visibleMembers.length === 0 && visibleInvites.length === 0 ? (
              <p className="m-0 rounded-2xl border border-dashed border-line px-4 py-10 text-center text-sm text-muted">
                {t("team.empty")}
              </p>
            ) : null}
          </div>
        ) : null}

        <AlertDialog
          open={nameTarget !== null}
          onOpenChange={(open) => {
            if (!open && !busy) setNameTarget(null);
          }}
        >
          <AlertDialogContent>
            <AlertDialogTitle>{t("team.editNameTitle")}</AlertDialogTitle>
            <AlertDialogDescription>{t("app.account.yourNameHelp")}</AlertDialogDescription>
            <label className="grid gap-1.5">
              <span className="text-[0.75rem] font-medium text-muted">
                {t("team.columns.name")}
              </span>
              <input
                value={nameValue}
                onChange={(event) => setNameValue(event.target.value)}
                maxLength={80}
                className="h-9.5 rounded-xl border border-line-strong bg-surface px-3.5 text-[0.8125rem] focus:border-[#163a37] focus:outline-none"
                data-testid="team-name-input"
              />
            </label>
            <AlertDialogActions>
              <Button
                variant="outline"
                size="sm"
                disabled={busy}
                onClick={() => setNameTarget(null)}
              >
                {t("common.cancel")}
              </Button>
              <Button
                size="sm"
                disabled={busy || nameValue.trim().length === 0}
                data-testid="team-name-save"
                onClick={() => {
                  if (!nameTarget) return;
                  setBusy(true);
                  void updateMemberDisplayName(
                    organizationId,
                    nameTarget.membershipId,
                    nameValue.trim(),
                  )
                    .then((updated) => {
                      if (nameTarget.isCurrentUser) {
                        applyMemberDisplayName(organizationId, updated.displayName);
                      }
                      pushNotification({ message: t("team.nameUpdated") });
                      setNameTarget(null);
                      reload();
                    })
                    .catch((caught: unknown) => {
                      pushNotification({
                        kind: "error",
                        message: isApiError(caught) ? caught.message : t("team.actionError"),
                      });
                    })
                    .finally(() => setBusy(false));
                }}
              >
                {t("common.save")}
              </Button>
            </AlertDialogActions>
          </AlertDialogContent>
        </AlertDialog>

        <InviteMembersDialog
          open={inviteOpen}
          organizationId={organizationId}
          actorRole={role}
          onOpenChange={setInviteOpen}
          onCreated={() => {
            pushNotification({ message: t("team.invite.savedNotSent") });
            reload();
          }}
        />

        <AlertDialog
          open={confirm !== null}
          onOpenChange={(open) => {
            if (!open && !busy) setConfirm(null);
          }}
        >
          <AlertDialogContent>
            <div className="flex items-start gap-3 text-start">
              <span
                className="inline-grid size-10 shrink-0 place-items-center rounded-full bg-error-muted text-destructive"
                aria-hidden="true"
              >
                {confirm?.kind === "remove" ? <UserMinus size={18} /> : <MailX size={18} />}
              </span>
              <div className="grid min-w-0 gap-1">
                <AlertDialogTitle>
                  {confirm?.kind === "remove"
                    ? t("team.removeTitle")
                    : t("team.cancelInviteTitle")}
                </AlertDialogTitle>
                <AlertDialogDescription>
                  {confirm?.kind === "remove"
                    ? t("team.removeBody", {
                        name: confirm.member.displayName,
                        email: confirm.member.email,
                      })
                    : t("team.cancelInviteBody", {
                        email: confirm?.kind === "cancel" ? confirm.invitation.email : "",
                      })}
                </AlertDialogDescription>
              </div>
            </div>
            <AlertDialogActions>
              <Button
                variant="outline"
                size="sm"
                disabled={busy}
                onClick={() => setConfirm(null)}
              >
                {t("common.cancel")}
              </Button>
              <Button
                variant="destructive"
                size="sm"
                disabled={busy}
                onClick={() => void confirmAction()}
              >
                {confirm?.kind === "remove"
                  ? t("team.remove")
                  : t("team.cancelInviteConfirm")}
              </Button>
            </AlertDialogActions>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </SettingsSectionPage>
  );
}
